import { FC, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useTranslation } from "react-i18next";

import {
  useValidatePromoCode,
  ValidatedPromoCode,
} from "../../../api/billing";

interface Props {
  companyId: number;
  /** Restricts validation to the selected plan; codes can be plan-scoped. */
  planId?: number | null;
  /** Lifted so the parent can send the code with the checkout request. */
  value: ValidatedPromoCode | null;
  onChange: (promo: ValidatedPromoCode | null) => void;
  disabled?: boolean;
}

/**
 * Promo code entry for providers that expect the code from us.
 *
 * Only rendered when `capabilities.promo_collected_by === "app"`. Stripe's
 * hosted page has its own promo field, so showing this alongside it would give
 * a customer two places to enter a code — and nothing would reconcile the case
 * where they enter different ones.
 *
 * Validation here is advisory. The API re-checks the code inside the checkout
 * call, so the discount is always decided server-side; this exists so someone
 * who mistypes a code finds out at the field rather than after a redirect.
 */
export const PromoCodeInput: FC<Props> = ({
  companyId,
  planId,
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const validate = useValidatePromoCode(companyId);

  const apply = () => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setError(null);
    validate.mutate(
      { code: trimmed, plan_id: planId ?? null },
      {
        onSuccess: (result) => {
          if (result.valid && result.promo_code) {
            onChange(result.promo_code);
            setCode("");
          } else {
            // The API returns a specific reason ("this code expired on 12 May").
            // Surfaced verbatim: a generic "invalid code" for an expired one is
            // what turns a self-service fix into a support ticket.
            setError(
              result.message ??
                t("billing.promoInvalid", "Ce code promo n'est pas valable.")
            );
          }
        },
        onError: () => {
          setError(t("common.errorGlobal", "Une erreur est survenue."));
        },
      }
    );
  };

  // ── Applied ───────────────────────────────────────────────────────────
  if (value) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip
            color="success"
            variant="outlined"
            icon={<CheckCircleRoundedIcon />}
            label={`${value.code} — ${value.discount_label}`}
          />
          <Button
            size="small"
            variant="text"
            disabled={disabled}
            onClick={() => {
              onChange(null);
              setError(null);
            }}
          >
            {t("common.remove", "Retirer")}
          </Button>
        </Stack>

        {/*
          How long the discount lasts is stated explicitly rather than left to
          the customer to infer from the percentage. "20%" reads as permanent;
          a code that only covers the first cycle has to say so before the card
          is handed over, not on the second invoice.
        */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
          {value.duration === "forever"
            ? t("billing.promoForever", "Réduction appliquée à chaque échéance.")
            : value.duration === "once"
            ? t("billing.promoOnce", "Réduction appliquée à la première échéance uniquement.")
            : t("billing.promoRepeating", {
                defaultValue:
                  "Réduction appliquée pendant {{count}} mois, puis tarif normal.",
                count: value.duration_in_months ?? 0,
              })}
        </Typography>
      </Box>
    );
  }

  // ── Entry ─────────────────────────────────────────────────────────────
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          label={t("billing.promoLabel", "Code promo")}
          value={code}
          disabled={disabled || validate.isPending}
          error={!!error}
          helperText={error ?? " "}
          // Codes are stored uppercase and matched case-insensitively;
          // normalising as the customer types avoids a rejection that looks
          // arbitrary to someone who typed the code exactly as printed.
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              apply();
            }
          }}
          inputProps={{ maxLength: 40, autoCapitalize: "characters" }}
        />
        <Button
          variant="outlined"
          sx={{ mt: 0.25 }}
          disabled={disabled || validate.isPending || !code.trim()}
          onClick={apply}
          startIcon={
            validate.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
          }
        >
          {t("billing.promoApply", "Appliquer")}
        </Button>
      </Stack>
    </Box>
  );
};

export default PromoCodeInput;
